'use client';

import { useEffect } from 'react';

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
  }
}

const ACTIVE_SCORE_NEED = 60;
const ACTIVE_SCORE_CHECK_TIME = 10;
const ACTIVE_SCORE_LOOP = false;
const ACTIVE_SCORE_COOKIE_NAME = '60sec_ap';
const ACTIVE_SCORE_EVENTS = [
  'touchmove',
  'blur',
  'focus',
  'focusin',
  'focusout',
  'load',
  'resize',
  'scroll',
  'unload',
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'mousemove',
  'mouseover',
  'mouseout',
  'mouseenter',
  'mouseleave',
  'change',
  'select',
  'submit',
  'keydown',
  'keypress',
  'keyup',
  'error',
] as const;

type YandexMetrikaEventsProps = {
  ymId?: string | number | null;
};

function getCookie(name: string) {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEq = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let index = 0; index < cookies.length; index += 1) {
    let currentCookie = cookies[index];

    while (currentCookie.charAt(0) === ' ') {
      currentCookie = currentCookie.substring(1, currentCookie.length);
    }

    if (currentCookie.indexOf(nameEq) === 0) {
      return currentCookie.substring(nameEq.length, currentCookie.length);
    }
  }

  return null;
}

function setCookie(name: string, value: string, days?: number) {
  if (typeof document === 'undefined') {
    return;
  }

  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${name}=${value || ''}${expires}; path=/`;
}

function hasRussianLetters(value: string) {
  return /[А-Яа-яЁё]/.test(value);
}

export default function YandexMetrikaEvents({ ymId }: YandexMetrikaEventsProps) {
  const metrikaId =
    typeof ymId === 'string' ? ymId.trim() : String(ymId || '').trim();

  useEffect(() => {
    if (!metrikaId) {
      return undefined;
    }

    const reachGoal = (goalId: string) => {
      if (typeof window.ym === 'function') {
        window.ym(metrikaId, 'reachGoal', goalId);
        return true;
      }

      console.log('Яндекс Метрика не инициализирована');
      return false;
    };

    const sendMetrikaEvent = () => {
      console.log(timer);
      console.log(ACTIVE_SCORE_NEED);
      console.log(getCookie(ACTIVE_SCORE_COOKIE_NAME));

      console.log('событие отправилось');
      reachGoal('60_sec');
    };

    const parts = Math.ceil(
      ACTIVE_SCORE_NEED / ACTIVE_SCORE_CHECK_TIME
    );
    const period = {
      start: 0,
      end: 0,
      events: false,
    };

    let activeParts = 0;
    let timer = 0;
    let timeoutId: number | null = null;

    const microtime = () => {
      const now = new Date().getTime() / 1000;
      return parseInt(String(now), 10);
    };

    const setPeriod = () => {
      period.start = microtime();
      period.end = period.start + ACTIVE_SCORE_CHECK_TIME;
      period.events = false;
    };

    const persistProgress = () => {
      setCookie(ACTIVE_SCORE_COOKIE_NAME, String(activeParts));
    };

    const send = () => {
      persistProgress();
      sendMetrikaEvent();
    };

    const checkSecs = () => {
      if (timer >= ACTIVE_SCORE_NEED) {
        send();

        if (ACTIVE_SCORE_LOOP) {
          timer = 0;
          activeParts = 0;
          return false;
        }

        return true;
      }

      return false;
    };

    const runPeriod = () => {
      timeoutId = window.setTimeout(() => {
        if (period.events) {
          activeParts += 1;
        }

        timer = activeParts * ACTIVE_SCORE_CHECK_TIME;
        console.log(`${activeParts} / ${parts} [${timer}]`);

        const completed = checkSecs();
        persistProgress();

        if (!completed) {
          setPeriod();
          runPeriod();
        }
      }, ACTIVE_SCORE_CHECK_TIME * 1000);
    };

    const start = () => {
      setPeriod();
      runPeriod();
    };

    const canStart = () => {
      const cookieValue = getCookie(ACTIVE_SCORE_COOKIE_NAME);

      if (cookieValue == null) {
        return true;
      }

      const parsedCookieValue = parseInt(cookieValue, 10);

      if (Number.isNaN(parsedCookieValue)) {
        return true;
      }

      if (parsedCookieValue >= parts) {
        return ACTIVE_SCORE_LOOP;
      }

      activeParts = parsedCookieValue;
      timer = activeParts * ACTIVE_SCORE_CHECK_TIME;
      return true;
    };

    const handleActivity = (event: Event) => {
      if (event.isTrusted && period.events === false) {
        period.events = true;
      }
    };

    if (!canStart()) {
      return undefined;
    }

    ACTIVE_SCORE_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity);
    });

    start();

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      ACTIVE_SCORE_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [metrikaId]);

  useEffect(() => {
    if (!metrikaId) {
      return undefined;
    }

    const sendMetrikaEvent = () => {
      if (typeof window.ym === 'function') {
        console.log('Отправка события в Яндекс Метрику');
        window.ym(metrikaId, 'reachGoal', 'emailCopied');
        return;
      }

      console.log('Яндекс Метрика не инициализирована');
    };

    const handleCopyEvent = () => {
      const copiedText = window.getSelection?.()?.toString() || '';
      console.log('Скопированный текст:', copiedText);

      const hasAtSymbol = copiedText.indexOf('@') !== -1;
      const containsRussianLetters = hasRussianLetters(copiedText);

      console.log('Содержит символ "@":', hasAtSymbol);
      console.log('Содержит русские буквы:', containsRussianLetters);

      if (hasAtSymbol && !containsRussianLetters) {
        sendMetrikaEvent();
      }
    };

    const handleMailtoRightClick = (event: MouseEvent) => {
      if (event.button !== 2) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const mailtoLink = target.closest('a[href^="mailto:"]');

      if (!mailtoLink) {
        return;
      }

      const href = mailtoLink.getAttribute('href') || '';
      const email = href.substring(7).split('?')[0];

      console.log('Клик правой кнопкой на mailto:', email);

      const containsRussianLetters = hasRussianLetters(email);
      console.log('Содержит русские буквы:', containsRussianLetters);

      if (!containsRussianLetters) {
        sendMetrikaEvent();
      }
    };

    document.addEventListener('copy', handleCopyEvent);
    document.addEventListener('contextmenu', handleMailtoRightClick);

    return () => {
      document.removeEventListener('copy', handleCopyEvent);
      document.removeEventListener('contextmenu', handleMailtoRightClick);
    };
  }, [metrikaId]);

  return null;
}
